/**
 * Bank Transfer Redux Slice
 * ==========================
 * Redux state management for bank transfer top-up functionality.
 *
 * Author: SuberPay Team
 * Date: 2026-09-14
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  BankAccount,
  BankTransferRequest,
  BankTransferMessage,
} from '../../../types/bank-transfer.types'

export interface BankTransferState {
  // Bank accounts
  bankAccounts: BankAccount[]
  bankAccountsLoading: boolean

  // Transfer requests
  transfers: BankTransferRequest[]
  transfersLoading: boolean
  transfersError: string | null

  // Current transfer details
  currentTransfer: BankTransferRequest | null
  currentTransferMessages: BankTransferMessage[]
  currentTransferLoading: boolean

  // Actions loading states
  initiatingTransfer: boolean
  uploadingProof: boolean
  sendingMessage: boolean

  // Filters
  statusFilter: string | null
  currentPage: number
  pageSize: number
  totalCount: number

  // General error
  error: string | null
}

const initialState: BankTransferState = {
  bankAccounts: [],
  bankAccountsLoading: false,

  transfers: [],
  transfersLoading: false,
  transfersError: null,

  currentTransfer: null,
  currentTransferMessages: [],
  currentTransferLoading: false,

  initiatingTransfer: false,
  uploadingProof: false,
  sendingMessage: false,

  statusFilter: null,
  currentPage: 1,
  pageSize: 20,
  totalCount: 0,

  error: null,
}

const bankTransferSlice = createSlice({
  name: 'bankTransfer',
  initialState,
  reducers: {
    // Bank Accounts
    fetchBankAccountsStart(state) {
      state.bankAccountsLoading = true
      state.error = null
    },
    fetchBankAccountsSuccess(state, action: PayloadAction<BankAccount[]>) {
      state.bankAccounts = action.payload
      state.bankAccountsLoading = false
    },
    fetchBankAccountsFailure(state, action: PayloadAction<string>) {
      state.bankAccountsLoading = false
      state.error = action.payload
    },

    // Transfer Requests List
    fetchTransfersStart(state) {
      state.transfersLoading = true
      state.transfersError = null
    },
    fetchTransfersSuccess(
      state,
      action: PayloadAction<{ transfers: BankTransferRequest[]; totalCount: number }>
    ) {
      state.transfers = action.payload.transfers
      state.totalCount = action.payload.totalCount
      state.transfersLoading = false
    },
    fetchTransfersFailure(state, action: PayloadAction<string>) {
      state.transfersLoading = false
      state.transfersError = action.payload
    },

    // Current Transfer Details
    fetchTransferDetailsStart(state) {
      state.currentTransferLoading = true
      state.error = null
    },
    fetchTransferDetailsSuccess(
      state,
      action: PayloadAction<{
        transfer: BankTransferRequest
        messages: BankTransferMessage[]
      }>
    ) {
      state.currentTransfer = action.payload.transfer
      state.currentTransferMessages = action.payload.messages
      state.currentTransferLoading = false
    },
    fetchTransferDetailsFailure(state, action: PayloadAction<string>) {
      state.currentTransferLoading = false
      state.error = action.payload
    },

    // Initiate Transfer
    initiateTransferStart(state) {
      state.initiatingTransfer = true
      state.error = null
    },
    initiateTransferSuccess(state, action: PayloadAction<BankTransferRequest>) {
      state.initiatingTransfer = false
      state.currentTransfer = action.payload
      // Add to transfers list
      state.transfers.unshift(action.payload)
    },
    initiateTransferFailure(state, action: PayloadAction<string>) {
      state.initiatingTransfer = false
      state.error = action.payload
    },

    // Upload Proof
    uploadProofStart(state) {
      state.uploadingProof = true
      state.error = null
    },
    uploadProofSuccess(state, action: PayloadAction<BankTransferRequest>) {
      state.uploadingProof = false
      state.currentTransfer = action.payload
      // Update in transfers list
      const index = state.transfers.findIndex((t) => t.id === action.payload.id)
      if (index !== -1) {
        state.transfers[index] = action.payload
      }
    },
    uploadProofFailure(state, action: PayloadAction<string>) {
      state.uploadingProof = false
      state.error = action.payload
    },

    // Send Message
    sendMessageStart(state) {
      state.sendingMessage = true
      state.error = null
    },
    sendMessageSuccess(state, action: PayloadAction<BankTransferMessage>) {
      state.sendingMessage = false
      // Add message to current transfer messages
      state.currentTransferMessages.push(action.payload)
    },
    sendMessageFailure(state, action: PayloadAction<string>) {
      state.sendingMessage = false
      state.error = action.payload
    },

    // Filters
    setStatusFilter(state, action: PayloadAction<string | null>) {
      state.statusFilter = action.payload
      state.currentPage = 1 // Reset to first page
    },
    setCurrentPage(state, action: PayloadAction<number>) {
      state.currentPage = action.payload
    },
    setPageSize(state, action: PayloadAction<number>) {
      state.pageSize = action.payload
      state.currentPage = 1 // Reset to first page
    },

    // Clear current transfer
    clearCurrentTransfer(state) {
      state.currentTransfer = null
      state.currentTransferMessages = []
    },

    // Clear error
    clearError(state) {
      state.error = null
      state.transfersError = null
    },
  },
})

export const {
  fetchBankAccountsStart,
  fetchBankAccountsSuccess,
  fetchBankAccountsFailure,

  fetchTransfersStart,
  fetchTransfersSuccess,
  fetchTransfersFailure,

  fetchTransferDetailsStart,
  fetchTransferDetailsSuccess,
  fetchTransferDetailsFailure,

  initiateTransferStart,
  initiateTransferSuccess,
  initiateTransferFailure,

  uploadProofStart,
  uploadProofSuccess,
  uploadProofFailure,

  sendMessageStart,
  sendMessageSuccess,
  sendMessageFailure,

  setStatusFilter,
  setCurrentPage,
  setPageSize,

  clearCurrentTransfer,
  clearError,
} = bankTransferSlice.actions

export default bankTransferSlice.reducer
